import { useCallback, useEffect, useRef } from "react";
import { useFormInternalContext } from "../../visual/FormProvider/formContext.js";
const wait = (delay) => new Promise((resolve) => void setTimeout(resolve, delay));
function useOnSubmitForm(submit, config) {
  const formApi = useFormInternalContext().api;
  const { retryDelay = 100, maxRetries = 0, keepFormEnabled = false } = config ?? {};
  const rContext = useRef({ submit, retryDelay, maxRetries, keepFormEnabled, attempts: 0 });
  useEffect(() => void Object.assign(rContext.current, { submit, retryDelay, maxRetries, keepFormEnabled }));
  const onSubmit = useCallback(
    (formEvent) => {
      const formDisabledState = formApi.getMeta().disabled;
      function restoreForm() {
        formApi.setSubmitting(false);
        if (!rContext.current.keepFormEnabled && !formDisabledState) formApi.setDisabled(false);
      }
      async function tryToSubmit() {
        try {
          formApi.setSubmitting(true);
          if (!rContext.current.keepFormEnabled && !formDisabledState) formApi.setDisabled(true);
          await formApi.waitForValidation();
          const status = formApi.getMeta().status;
          const valid = status === "indeterminate" || status === "pristine" ? await formApi.validate() : status === "valid";
          let returnedValue;
          if (valid) {
            returnedValue = await rContext.current.submit(
              formApi.getValues(),
              formApi.getFieldValues(),
              formApi,
              formEvent
            );
            restoreForm();
            return { value: returnedValue, status: "success" };
          }
          restoreForm();
          return { status: "invalid" };
        } catch (e) {
          const error = e instanceof Error ? e : typeof e === "string" ? new Error(e) : new Error("[useOnSubmitForm] Unknown Error");
          rContext.current.attempts += 1;
          if (rContext.current.maxRetries > 0) {
            const delay = typeof rContext.current.retryDelay === "function" ? rContext.current.retryDelay(rContext.current.attempts) : rContext.current.retryDelay;
            const stopRetries = rContext.current.attempts > rContext.current.maxRetries;
            if (stopRetries) {
              restoreForm();
              return { status: "max-retries", error };
            }
            await wait(delay);
            return tryToSubmit();
          }
          restoreForm();
          return { status: "error", error };
        }
      }
      if (formApi.getMeta().submitting) return Promise.resolve({ status: "pending" });
      return tryToSubmit();
    },
    [formApi]
  );
  return { onSubmit, formApi };
}
export {
  useOnSubmitForm
};
//# sourceMappingURL=useOnSubmitForm.js.map
